/**
 * Panel lateral deslizante y diálogo de confirmación.
 *
 * POR QUÉ PANEL LATERAL Y NO MODAL CENTRADO
 *
 * El usuario necesita seguir viendo la lista de contexto mientras da de alta un inmueble. Un
 * modal grande centrado sobre una tabla densa tapa justo lo que se estaba consultando.
 *
 * ACCESIBILIDAD, QUE AQUÍ NO ES OPCIONAL:
 *  · El foco queda ATRAPADO dentro del panel: tabular no debe llevarte a la tabla de detrás,
 *    que sigue ahí pero es inalcanzable con el ratón.
 *  · `Esc` cierra.
 *  · Al cerrar, el foco VUELVE al elemento que lo abrió. Sin esto, quien navega con teclado
 *    aparece al principio de la página y tiene que rehacer todo el recorrido.
 *  · El fondo queda `aria-hidden` para los lectores de pantalla.
 *
 * CAMBIOS SIN GUARDAR: si `dirty` está activo, cerrar pide confirmación. Se cubren las tres
 * vías de salida (botón, `Esc` y clic en el fondo), porque tapar solo una da una falsa
 * sensación de seguridad.
 */
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../Button';
import { Icon } from '../Icon';
import styles from './Drawer.module.css';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface DrawerProps {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** Hay cambios sin guardar: cerrar pedirá confirmación. */
  dirty?: boolean;
  wide?: boolean;
  /**
   * Oportunidad para que el contenido use `Esc` antes que el panel. Devuelve `true` si lo ha
   * consumido —por ejemplo, cerrando un desplegable propio— y entonces el panel no se cierra.
   *
   * Hace falta porque el atajo se escucha en `document` EN CAPTURA y con `stopPropagation`:
   * eso es lo correcto para que `Esc` funcione con el foco donde sea, pero significa que un
   * `onKeyDown` puesto en un campo de dentro NUNCA llega a ejecutarse. Sin este gancho, cerrar
   * un desplegable con `Esc` acaba preguntando si quieres descartar el formulario entero.
   */
  onEscape?: () => boolean;
}

export function Drawer({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer,
  dirty = false,
  wide = false,
  onEscape,
}: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const [confirmingClose, setConfirmingClose] = useState(false);
  // En una `ref` para que el efecto del teclado no se resuscriba en cada render del padre.
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  const requestClose = useCallback(() => {
    if (dirty) {
      setConfirmingClose(true);
      return;
    }
    onClose();
  }, [dirty, onClose]);

  // Se recuerda quién abrió el panel para devolverle el foco al cerrar.
  useEffect(() => {
    if (open) {
      openerRef.current = document.activeElement as HTMLElement | null;
      // El primer campo recibe el foco: quien abre un formulario quiere escribir.
      const timer = setTimeout(() => {
        const first = panelRef.current?.querySelector<HTMLElement>(FOCUSABLE);
        first?.focus();
      }, 60);
      return () => clearTimeout(timer);
    }
    openerRef.current?.focus();
    return undefined;
  }, [open]);

  // El fondo no debe poder desplazarse mientras el panel está abierto.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        // Lo de dentro tiene preferencia: un desplegable abierto se cierra primero.
        if (onEscapeRef.current?.()) return;
        requestClose();
        return;
      }

      // Trampa de foco: al llegar al último elemento, Tab vuelve al primero.
      if (event.key !== 'Tab') return;
      const focusables = panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (!focusables || focusables.length === 0) return;

      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, requestClose]);

  if (!open) return null;

  return createPortal(
    <>
      <div className={styles.scrim} onClick={requestClose} aria-hidden="true" />

      <div
        className={[styles.drawer, wide ? styles.wide : ''].filter(Boolean).join(' ')}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={panelRef}
      >
        <header className={styles.header}>
          <div>
            <h2 className={styles.title} id={titleId}>
              {title}
            </h2>
            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
          </div>
          <button type="button" className={styles.close} onClick={requestClose} aria-label="Cerrar">
            <Icon name="chevron-right" size={18} />
          </button>
        </header>

        <div className={styles.body}>{children}</div>

        {footer && <footer className={styles.footer}>{footer}</footer>}
      </div>

      {confirmingClose && (
        <ConfirmDialog
          title="Tienes cambios sin guardar"
          body="Si cierras ahora se perderán. ¿Seguro que quieres descartarlos?"
          confirmLabel="Descartar cambios"
          tone="danger"
          onCancel={() => setConfirmingClose(false)}
          onConfirm={() => {
            setConfirmingClose(false);
            onClose();
          }}
        />
      )}
    </>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Diálogo de confirmación
// ---------------------------------------------------------------------------

interface ConfirmDialogProps {
  title: string;
  body: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'default' | 'danger';
  /**
   * Recibe el texto escrito en la confirmación fuerte. Las rescisiones lo reenvían al
   * servidor, que lo vuelve a comprobar: la barrera no puede vivir solo en el formulario.
   */
  onConfirm: (typed: string) => void | Promise<void>;
  onCancel: () => void;
  /**
   * Contenido entre el cuerpo y la confirmación. Es donde va la PREVISUALIZACIÓN del impacto:
   * cargos que se anulan, renta indexada, fianza pendiente. Un diálogo que pide confirmar sin
   * enseñar consecuencias convierte una decisión en una apuesta.
   */
  children?: ReactNode;
  /**
   * Confirmación FUERTE: obliga a escribir este texto exacto, al estilo de los borrados
   * peligrosos de GitHub. Se reserva para lo que no se puede deshacer — borrar un inmueble,
   * rescindir un contrato —; para lo demás está el «Deshacer» del aviso, que protege igual y
   * no interrumpe.
   */
  requireText?: string;
  requireTextHint?: ReactNode;
  /** Mientras llega la previsualización, o si lo que enseña impide seguir. */
  confirmDisabled?: boolean;
}

export function ConfirmDialog({
  title,
  body,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  tone = 'default',
  onConfirm,
  onCancel,
  children,
  requireText,
  requireTextHint,
  confirmDisabled = false,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('');
  const [running, setRunning] = useState(false);
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      /*
       * El foco entra en el campo de confirmación si lo hay y, si no, en «Cancelar»; NUNCA en
       * el botón de la acción, aunque ahora vaya el primero: un Enter sin pensar no puede
       * borrar nada.
       */
      const dialog = dialogRef.current;
      (
        dialog?.querySelector<HTMLElement>('input, textarea') ??
        dialog?.querySelector<HTMLElement>('[data-dialog-cancel]') ??
        dialog?.querySelector<HTMLElement>(FOCUSABLE)
      )?.focus();
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCancel();
      }
    }
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onCancel]);

  const canConfirm =
    !running &&
    !confirmDisabled &&
    (!requireText || typed.trim().toLowerCase() === requireText.trim().toLowerCase());

  return createPortal(
    <>
      <div className={styles.scrim} style={{ zIndex: 70 }} onClick={onCancel} aria-hidden="true" />
      <div
        className={styles.dialog}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={dialogRef}
      >
        <h2 className={styles.dialogTitle} id={titleId}>
          {title}
        </h2>
        <p className={styles.dialogBody}>{body}</p>

        {children && <div className={styles.dialogContent}>{children}</div>}

        {requireText && (
          <label className={styles.confirmField}>
            <span className={styles.confirmHint}>
              {requireTextHint ?? (
                <>
                  Para confirmar, escribe <code>{requireText}</code>
                </>
              )}
            </span>
            <input
              className={styles.confirmInput}
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              placeholder={requireText}
              autoComplete="off"
              spellCheck={false}
            />
          </label>
        )}

        {/* La acción a la izquierda y «Cancelar» a la derecha, en todos los diálogos. */}
        <div className={styles.dialogActions}>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            size="sm"
            disabled={!canConfirm}
            onClick={async () => {
              setRunning(true);
              try {
                await onConfirm(typed);
              } finally {
                setRunning(false);
              }
            }}
          >
            {running ? 'Un momento…' : confirmLabel}
          </Button>
          <Button variant="secondary" size="sm" onClick={onCancel} disabled={running} data-dialog-cancel>
            {cancelLabel}
          </Button>
        </div>
      </div>
    </>,
    document.body,
  );
}

/**
 * Pasos del asistente. Marca el actual y los completados; los futuros no son pulsables,
 * porque saltarse un paso deja el formulario en un estado que el servidor va a rechazar.
 */
export function Stepper({
  steps,
  current,
  onGoTo,
  free = false,
}: {
  steps: string[];
  current: number;
  onGoTo?: (index: number) => void;
  /**
   * Todos los pasos accesibles, también los de delante. Es para EDITAR: ahí los datos ya
   * existen y obligar a pasar por «Identificación» y «Características» para tocar una
   * habitación no protege nada. En el alta se queda en `false`: un paso no se salta hasta que
   * el anterior es válido.
   */
  free?: boolean;
}) {
  return (
    <ol className={styles.stepper}>
      {steps.map((step, index) => {
        const state = index === current ? 'current' : index < current || free ? 'done' : 'todo';
        const reachable = (free ? index !== current : index < current) && onGoTo;
        return (
          <li key={step} className={styles.step} data-state={state}>
            <button
              type="button"
              className={styles.stepButton}
              disabled={!reachable}
              onClick={() => reachable && onGoTo(index)}
              aria-current={state === 'current' ? 'step' : undefined}
            >
              {/* Editando no hay pasos «hechos»: todos lo están. El número sigue diciendo dónde estás. */}
              <span className={styles.stepDot}>{index < current && !free ? '✓' : index + 1}</span>
              <span className={styles.stepLabel}>{step}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}
